import { formatDate } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    FieldError,
    TST_COLUMN_SIZE,
    TST_DEFAULT_PAGE_SIZE,
} from "@impero-com/ui";
import {
    createColumnHelper,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFacetedUniqueValues,
    getSortedRowModel,
    useReactTable,
    getPaginationRowModel,
} from "@tanstack/react-table";
import { Typography } from "antd";
import { FieldErrors, Control, useWatch, FieldPath } from "react-hook-form";

import type {
    SampleConclusionAnswerKind,
    SampleConclusionFieldKind,
    SavedBlob,
} from "api/generated/model";

import { ActivitySample, TestingActivityForm } from "../../schema";
import { ActivityField, ActivityFields, ControlSample } from "../../types";

import { BlobLinkList } from "./BlobLinkList";
import { useControlActivityStatusLabelMap } from "./consts";

const { Link } = Typography;

const VIEWED_COLUMN_ID = "viewed";
const TITLE_COLUMN_ID = "title";
const DUE_DATE_COLUMN_ID = "dueDate";
const RESPONSIBLE_COLUMN_ID = "responsible";
const STATUS_COLUMN_ID = "status";

const columnHelper = createColumnHelper<ControlSample>();

interface UseSelectedSamplesTableProps {
    data: ControlSample[];
    renderViewedIndicator: (sample: ControlSample) => React.ReactNode;
    setOpenSampleDrawerId: (id: string | null) => void;
    sampleErrors: FieldErrors<ActivitySample[]> | undefined;
    getSampleFieldIndex: (activityId: string) => number;
    sampleConclusionFields: ActivityFields;
    control: Control<TestingActivityForm>;
    blobs: SavedBlob[];
}

export function useSelectedSamplesTable({
    data,
    renderViewedIndicator,
    setOpenSampleDrawerId,
    sampleErrors,
    getSampleFieldIndex,
    sampleConclusionFields,
    blobs,
    control,
}: UseSelectedSamplesTableProps) {
    const { t } = useTranslation();
    const controlActivityStatusLabelMap = useControlActivityStatusLabelMap();

    const sampleFieldColumns = sampleConclusionFields.map(
        (sampleConclusionField) => {
            return columnHelper.accessor(
                (field) => field.answers?.[sampleConclusionField.id]?.value,
                {
                    id: getSampleAnswerColumnId(sampleConclusionField),
                    header: sampleConclusionField.title,
                    cell: (info) => (
                        <WatchValue
                            control={control}
                            name={`samples.${getSampleFieldIndex(
                                info.row.original.activityId
                            )}.fields.${sampleConclusionField.id}`}
                            fieldKind={
                                sampleConclusionField.fieldType as SampleConclusionFieldKind
                            }
                            blobs={blobs}
                        />
                    ),
                    size: TST_COLUMN_SIZE.L,
                }
            );
        }
    );

    const columns = [
        columnHelper.display({
            id: VIEWED_COLUMN_ID,
            header: "",
            cell: (info) => renderViewedIndicator(info.row.original),
            enableSorting: false,
            size: TST_COLUMN_SIZE.CHECKBOX,
            meta: { fixedWidth: true },
        }),
        columnHelper.accessor((row) => row.title, {
            id: TITLE_COLUMN_ID,
            header: t("ControlTestingActivityView_NameColumn"),
            size: TST_COLUMN_SIZE.XL,
            enableHiding: false,
            cell: (info) => {
                const sampleIndex = getSampleFieldIndex(
                    info.row.original.activityId
                );
                const hasError = sampleErrors?.[sampleIndex] !== undefined;
                return (
                    <>
                        <Link
                            onClick={() =>
                                setOpenSampleDrawerId(
                                    info.row.original.activityId
                                )
                            }
                        >
                            {info.getValue()}
                        </Link>
                        <FieldError
                            error={
                                hasError
                                    ? t(
                                          "ControlTestingActivityView_DrawerError"
                                      )
                                    : undefined
                            }
                        />
                    </>
                );
            },
            enableSorting: true,
        }),
        columnHelper.accessor((row) => row.dueDate, {
            id: DUE_DATE_COLUMN_ID,
            header: t("ControlTestingActivityView_DueDateColumn"),
            cell: (info) =>
                info.getValue() ? formatDate(info.getValue() as Date) : "",
            enableSorting: true,
        }),
        columnHelper.accessor((row) => row.responsible.label, {
            id: RESPONSIBLE_COLUMN_ID,
            header: t("ControlTestingActivityView_ResponsibleColumn"),
            cell: (info) => info.getValue(),
            enableSorting: true,
        }),
        columnHelper.accessor((row) => row.controlStatus, {
            id: STATUS_COLUMN_ID,
            header: t("ControlTestingActivityView_StatusColumn"),
            cell: (info) => controlActivityStatusLabelMap[info.getValue()],
            enableSorting: true,
        }),
        ...sampleFieldColumns,
    ];

    const table = useReactTable({
        data,
        columns,
        initialState: {
            sorting: [
                {
                    id: TITLE_COLUMN_ID,
                    desc: false,
                },
            ],
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
            },
            columnPinning: {
                left: [VIEWED_COLUMN_ID],
            },
            columnVisibility: getInitialSampleFieldVisibility(
                sampleConclusionFields
            ),
        },
        enableSortingRemoval: false,
        getCoreRowModel: getCoreRowModel<ControlSample>(),
        getSortedRowModel: getSortedRowModel<ControlSample>(),
        getFacetedMinMaxValues: getFacetedMinMaxValues<ControlSample>(),
        getFacetedUniqueValues: getFacetedUniqueValues<ControlSample>(),
        getPaginationRowModel: getPaginationRowModel<ControlSample>(),
    });

    return {
        table,
    };
}

function getInitialSampleFieldVisibility(
    sampleConclusionFields: ActivityFields
) {
    return sampleConclusionFields.reduce((acc, field) => {
        const id = getSampleAnswerColumnId(field);
        return {
            ...acc,
            [id]: false,
        };
    }, {});
}

function getSampleAnswerColumnId(activityField: ActivityField) {
    return `answer_${activityField.id}`;
}

interface WatchValueProps {
    name: FieldPath<TestingActivityForm>;
    control: Control<TestingActivityForm>;
    fieldKind: SampleConclusionFieldKind;
    blobs: SavedBlob[];
}

function WatchValue({ control, name, fieldKind, blobs }: WatchValueProps) {
    const value = useWatch({
        control,
        name,
    }) as SampleConclusionAnswerKind["value"];

    return <>{formatAnswerForTable(value, fieldKind, blobs)}</>;
}

function formatAnswerForTable(
    value: SampleConclusionAnswerKind["value"] | undefined,
    fieldKind: SampleConclusionFieldKind,
    blobs: SavedBlob[]
) {
    if (!value) {
        return "";
    }
    switch (fieldKind.type) {
        case "CheckboxGroup": {
            const typedValue = value as string[];
            return typedValue.join(", ");
        }
        case "Dropdown": {
            const typedValue = value as string;
            return typedValue;
        }
        case "Heading": {
            const typedValue = value as string;
            return typedValue;
        }
        case "NumericInput": {
            const typedValue = value as number;
            return typedValue.toString();
        }
        case "TextInput": {
            const typedValue = value as string;
            return typedValue;
        }
        case "FileUpload": {
            const typedValue = value as string[];
            return (
                <BlobLinkList
                    key={typedValue.length}
                    blobs={blobs}
                    blobIds={typedValue}
                />
            );
        }
        case "RadioButtonGroup": {
            const typedValue = value as string;
            return typedValue;
        }
        case "TextArea": {
            const typedValue = value as string;
            return typedValue;
        }
    }
}
